import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import {
  Rocket, Package, FileText, Code, CreditCard, BarChart3, Users, ShoppingCart,
  ChevronLeft, ChevronRight, CheckCircle2
} from 'lucide-react';

const STORAGE_KEY = 'funnely_onboarding_completed_v1';

const STEPS = [
  {
    icon: Rocket,
    title: 'Welcome to Funnely!',
    subtitle: 'Your all-in-one platform for selling digital products',
    description: "Let's walk through the key features in under 2 minutes. You can replay this tour anytime from the top-right icon.",
    image: null,
    ctaLabel: "Let's go",
  },
  {
    icon: Package,
    title: '1. Products',
    subtitle: 'Upload your digital files once, sell unlimited times',
    description: 'Add ebooks, courses, templates, or any digital file. Set your price, download limits, and bundle multiple products together for higher-value offers.',
    image: '/tutorial/products.png',
    tips: [
      'Upload PDF, ZIP, MP4, or any digital file',
      'Set download expiry and limits',
      'Bundle products for higher AOV',
    ],
  },
  {
    icon: FileText,
    title: '2. Landing Pages',
    subtitle: 'Your beautifully-designed product store',
    description: 'Build a landing page using ready templates or let our AI do it for you. Customize colors, copy, hero section, and testimonials — no coding needed.',
    image: '/tutorial/landing-pages.png',
    tips: [
      'Pick a template or start from scratch',
      'AI Builder generates pages in seconds (Premium)',
      'Share your page link in your bio',
    ],
  },
  {
    icon: Code,
    title: '3. Checkout Forms',
    subtitle: 'Embed checkout anywhere on the web',
    description: 'Build custom checkout forms and embed them on your own website, blog, or WhatsApp. Customize fields, branding, and payment methods.',
    image: '/tutorial/checkout-forms.png',
    tips: [
      'Embed via iframe on any website',
      'Accept FPX, card, e-wallet',
      'Custom branding per form',
    ],
  },
  {
    icon: Users,
    title: '4. Customers',
    subtitle: 'Your buyer database, all in one place',
    description: 'Every purchase auto-creates a customer record with email, total spent, and order history. Build your loyal audience over time.',
    image: '/tutorial/customers.png',
    tips: [
      'Auto-captured from each purchase',
      'See lifetime value per customer',
      'Export for email marketing campaigns',
    ],
  },
  {
    icon: ShoppingCart,
    title: '5. Orders',
    subtitle: 'Full order history with download tracking',
    description: 'Track every order, payment status, and download activity. Revoke access, resend receipts, or issue manual adjustments when needed.',
    image: '/tutorial/orders.png',
    tips: [
      'Real-time payment status',
      'Track download count per customer',
      'Revoke access for refunded orders',
    ],
  },
  {
    icon: CreditCard,
    title: '6. Payment Settings',
    subtitle: 'Connect your gateway to start receiving payments',
    description: 'Connect Billplz, ToyyibPay, SenangPay, or Stripe. Funds go directly to your account — Funnely never holds your money.',
    image: '/tutorial/payment-settings.png',
    tips: [
      'Supports 4 Malaysian gateways',
      'Money flows directly to you',
      'Connect minimum 1 to enable checkout',
    ],
  },
  {
    icon: BarChart3,
    title: '7. Sales Report',
    subtitle: 'Track revenue, orders, and conversions',
    description: 'See exactly what is selling, your best landing pages, top customers, and conversion rates. Export to CSV/Excel for accounting.',
    image: '/tutorial/sales-report.png',
    tips: [
      'Revenue trends over time',
      'Top products and customers',
      'Export for accountant or tax',
    ],
  },
  {
    icon: CheckCircle2,
    title: "You're all set!",
    subtitle: 'Start selling in 10 minutes',
    description: 'Head to Products to upload your first digital product, then create a landing page and share the link. You have got this!',
    image: null,
    ctaLabel: 'Start selling',
  },
];

export default function OnboardingTutorial({ open, onClose }) {
  const [step, setStep] = useState(0);

  useEffect(() => { if (open) setStep(0); }, [open]);

  const current = STEPS[step];
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;
  const Icon = current.icon;

  const next = () => { if (isLast) finish(); else setStep(step + 1); };
  const prev = () => { if (!isFirst) setStep(step - 1); };
  const finish = () => {
    try { localStorage.setItem(STORAGE_KEY, new Date().toISOString()); } catch (_) {}
    onClose();
  };
  const skip = () => finish();

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) finish(); }}>
      <DialogContent
        className="sm:max-w-[720px] p-0 overflow-hidden gap-0"
        style={{ border: '1.5px solid #195259', borderRadius: '20px' }}
        data-testid="onboarding-tutorial-dialog"
      >
        {/* Top accent bar with step progress */}
        <div className="h-1.5 w-full" style={{ backgroundColor: '#F3F4F6' }}>
          <div
            className="h-full transition-all duration-300"
            style={{
              width: `${((step + 1) / STEPS.length) * 100}%`,
              backgroundColor: '#FF9A00',
            }}
          />
        </div>

        {/* Header */}
        <div className="px-8 pt-8 pb-4 flex items-start gap-4">
          <div
            className="h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#FF9A00' }}
          >
            <Icon className="h-6 w-6" style={{ color: '#195259' }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#FF9A00' }}>
              Step {step + 1} of {STEPS.length}
            </p>
            <h2 className="text-xl font-bold" style={{ color: '#195259' }}>{current.title}</h2>
            <p className="text-sm mt-1" style={{ color: '#6D6E71' }}>{current.subtitle}</p>
          </div>
        </div>

        {/* Screenshot (if provided) */}
        {current.image && (
          <div className="px-8 pb-4">
            <div
              className="rounded-xl overflow-hidden"
              style={{ border: '1.5px solid #195259', backgroundColor: '#FAFAFA' }}
            >
              <img
                src={current.image}
                alt={current.title}
                className="w-full h-[260px] object-cover object-top"
              />
            </div>
          </div>
        )}

        {/* Description + tips */}
        <div className="px-8 pb-6">
          <p className="text-sm leading-relaxed" style={{ color: '#195259' }}>
            {current.description}
          </p>
          {current.tips && (
            <ul className="mt-4 space-y-2">
              {current.tips.map((tip, i) => (
                <li key={i} className="flex items-start gap-2 text-sm" style={{ color: '#6D6E71' }}>
                  <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: '#FF9A00' }} />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer: nav buttons */}
        <div
          className="px-8 py-4 flex items-center justify-between gap-3"
          style={{ borderTop: '1px solid #E5E7EB', backgroundColor: '#FAFAFA' }}
        >
          <div className="flex gap-1.5">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className="h-1.5 rounded-full transition-all"
                style={{
                  width: i === step ? '24px' : '6px',
                  backgroundColor: i === step ? '#FF9A00' : (i < step ? '#195259' : '#E5E7EB'),
                }}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <Button
                variant="outline"
                size="sm"
                onClick={prev}
                data-testid="onboarding-prev-button"
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            )}
            {!isLast && !isFirst && (
              <button
                onClick={skip}
                className="text-xs font-medium px-3 py-2 hover:underline"
                style={{ color: '#6D6E71' }}
                data-testid="onboarding-skip-button"
              >
                Skip tour
              </button>
            )}
            <Button
              size="sm"
              onClick={next}
              className="font-semibold"
              style={{ backgroundColor: '#FF9A00', color: '#195259' }}
              data-testid="onboarding-next-button"
            >
              {current.ctaLabel || (isLast ? 'Get started' : 'Next')}
              {!isLast && <ChevronRight className="h-4 w-4 ml-1" />}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Helper: check if user has completed onboarding
export function hasCompletedOnboarding() {
  try {
    return !!localStorage.getItem(STORAGE_KEY);
  } catch (_) {
    return false;
  }
}
